/**
 * Error Classifier Utility
 * Converts API errors into user-facing messages with retry logic hints
 */

import { parseApiError } from "./parseApiError";

export const classifyError = (error, endpoint) => {
  const parsed = parseApiError(error);
  const status = parsed.status ?? error?.response?.status;

  // ===== NETWORK ERRORS (no HTTP status) =====
  if (!status) {
    const isTimeout = parsed.code === "TIMEOUT";
    return {
      status: isTimeout ? "timeout" : "network",
      message: parsed.message,
      reason: parsed.reason,
      code: parsed.code,
      retryable: true,
      endpoint,
      severity: isTimeout ? "warning" : "error",
    };
  }

  // ===== HTTP 400: Bad Request =====
  if (status === 400) {
    return {
      status: 400,
      message: parsed.message,
      reason: parsed.reason,
      code: parsed.code,
      retryable: false,
      endpoint,
      severity: "error",
    };
  }

  // ===== HTTP 401: Unauthorized =====
  if (status === 401) {
    return {
      status: 401,
      message: parsed.message,
      reason: parsed.reason,
      code: parsed.code,
      retryable: false,
      endpoint,
      severity: "error",
      requiresRedirect: true,
    };
  }

  // ===== HTTP 403: Forbidden =====
  if (status === 403) {
    return {
      status: 403,
      message: parsed.message,
      reason: parsed.reason,
      code: parsed.code,
      retryable: false,
      endpoint,
      severity: "error",
    };
  }

  // ===== HTTP 404: Not Found =====
  if (status === 404) {
    const endpointMessages = {
      "hierarchy-tree": {
        message: parsed.message || "Hierarchy not available. Showing flat view.",
        reason: parsed.reason,
        severity: "warning",
      },
      latest: {
        message: parsed.message || "No live data available for your account.",
        reason: parsed.reason,
        severity: "error",
      },
      status: {
        message: null,
        reason: null,
        severity: "silent",
      },
      get_forecasted_data: {
        message: parsed.message || "Forecasting data not available for this machine.",
        reason: parsed.reason,
        severity: "warning",
      },
      mape: {
        message: null,
        reason: null,
        severity: "silent",
      },
      "off-grid-calculator/scope": {
        message: parsed.message,
        reason: parsed.reason,
        severity: "error",
      },
      "off-grid-calculator/analyze": {
        message: parsed.message,
        reason: parsed.reason,
        severity: "error",
      },
    };

    const config = endpointMessages[endpoint] || {
      message: parsed.message || `Data not found for ${endpoint}.`,
      reason: parsed.reason,
      severity: "warning",
    };

    return {
      status: 404,
      message: config.message,
      reason: config.reason ?? parsed.reason,
      code: parsed.code,
      retryable: false,
      endpoint,
      severity: config.severity,
    };
  }

  // ===== HTTP 503: Service Unavailable (often database) =====
  if (status === 503) {
    return {
      status: 503,
      message: parsed.message,
      reason: parsed.reason,
      code: parsed.code,
      retryable: true,
      endpoint,
      severity: "error",
    };
  }

  // ===== HTTP 500: Server Error =====
  if (status === 500) {
    const endpointMessages = {
      "hierarchy-tree": {
        message: parsed.message || "Hierarchy temporarily unavailable.",
        reason: parsed.reason,
        severity: "error",
      },
      latest: {
        message: parsed.message || "Live data temporarily unavailable.",
        reason: parsed.reason,
        severity: "error",
      },
      status: {
        message: null,
        reason: null,
        severity: "silent",
      },
      get_forecasted_data: {
        message: parsed.message || "Forecast service error. Try again later.",
        reason: parsed.reason,
        severity: "error",
      },
      mape: {
        message: null,
        reason: null,
        severity: "silent",
      },
      "off-grid-calculator/scope": {
        message: parsed.message,
        reason: parsed.reason,
        severity: "error",
      },
      "off-grid-calculator/analyze": {
        message: parsed.message,
        reason: parsed.reason,
        severity: "error",
      },
    };

    const config = endpointMessages[endpoint] || {
      message: parsed.message || "Server error. Try again later.",
      reason: parsed.reason,
      severity: "error",
    };

    return {
      status: 500,
      message: config.message,
      reason: config.reason ?? parsed.reason,
      code: parsed.code,
      retryable: true,
      endpoint,
      severity: config.severity,
    };
  }

  // ===== Unknown Status Code =====
  return {
    status: status || "unknown",
    message: parsed.message,
    reason: parsed.reason,
    code: parsed.code,
    retryable: true,
    endpoint,
    severity: "error",
  };
};

/**
 * Determine if UI should display an error banner for this classified error
 */
export const shouldShowError = (classifiedError) => {
  if (!classifiedError) return false;
  return classifiedError.severity !== "silent";
};

/**
 * Determine if error is fatal (should prevent data display)
 */
export const isFatalError = (classifiedError) => {
  if (!classifiedError) return false;
  return classifiedError.status === 401 || classifiedError.status === 403;
};
